import React, { useState } from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { ShieldCheck, Cpu, CheckCircle2, Lock, Coins, Activity } from 'lucide-react';

export const Slide03Solution: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(1);

  const steps = [
    {
      id: 1,
      title: '1. Inbound Request',
      subtitle: 'Employee / App sends prompt',
      snippet: 'User: "Analyze John Doe (SSN: 123-45-6789, email: john@corp.com) Q3 revenue."',
      tag: 'Raw Enterprise Text',
      tagColor: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
    },
    {
      id: 2,
      title: '2. Ephemeral In-Memory Vault',
      subtitle: 'PII tokenized in sub-millisecond RAM',
      snippet: 'Sanitized: "Analyze [PERSON_01] (SSN: [SSN_01], email: [EMAIL_01]) Q3 revenue."',
      tag: 'Zero PII Leaked Upstream',
      tagColor: 'text-gold-500 bg-gold-500/10 border-gold-500/20',
    },
    {
      id: 3,
      title: '3. Upstream LLM & Cost Gate',
      subtitle: 'Dispatched to OpenAI / Anthropic',
      snippet: 'LLM Response: "[PERSON_01] generated $4.2M in Q3 across all subsidiaries."',
      tag: 'USD Budget Verified & Deducted',
      tagColor: 'text-purple-500 bg-purple-500/10 border-purple-500/20',
    },
    {
      id: 4,
      title: '4. Dynamic In-Memory Rehydration',
      subtitle: 'Original tokens restored to client',
      snippet: 'Final Delivery: "John Doe generated $4.2M in Q3 across all subsidiaries."',
      tag: 'Seamless User Experience',
      tagColor: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
    },
  ];

  return (
    <DeckSlideWrapper
      slideNumber={3}
      totalSlides={12}
      category="The Solution"
      title="The Workouse Gateway: Zero-Trust by Design"
      subtitle="A drop-in, sub-millisecond security & cost governor between your enterprise workforce and any LLM."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-5 items-stretch">
        {/* Left 4 Solution Features */}
        <div className="lg:col-span-5 flex flex-col justify-between space-y-2 sm:space-y-2.5">
          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex items-start gap-2.5 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gold-500/15 text-gold-800 dark:text-gold-400 flex items-center justify-center shrink-0 mt-0.5">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">In-Memory DLP Token Vault</h4>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                PII, secrets, and credentials replaced with synthetic tokens in RAM before leaving the enterprise boundary.
              </p>
            </div>
          </div>

          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex items-start gap-2.5 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gold-500/15 text-gold-800 dark:text-gold-400 flex items-center justify-center shrink-0 mt-0.5">
              <Coins className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">Atomic USD Budget Guardrails</h4>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                Sub-cent precision accounting with soft-overdraft handling, team limits, and auto-fallback routing.
              </p>
            </div>
          </div>

          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex items-start gap-2.5 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gold-500/15 text-gold-800 dark:text-gold-400 flex items-center justify-center shrink-0 mt-0.5">
              <Cpu className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">100% OpenAI-Compatible Proxy</h4>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                Zero code refactoring. Change <code>baseURL</code> in LangChain, LlamaIndex, or custom SDKs to instantly secure traffic.
              </p>
            </div>
          </div>

          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex items-start gap-2.5 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gold-500/15 text-gold-800 dark:text-gold-400 flex items-center justify-center shrink-0 mt-0.5">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">Immutable Token Attribution</h4>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                Every request tagged to exact user identity (SAML/LDAP) with cryptographic tamper-evident logging.
              </p>
            </div>
          </div>
        </div>

        {/* Right Interactive Pipeline Visualizer */}
        <div className="lg:col-span-7 flex flex-col justify-between p-4 sm:p-5 rounded-2xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 shadow-surface relative overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-800 dark:border-navy-800 pb-2.5 sm:pb-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
              <span className="font-mono text-xs font-bold text-white tracking-wide uppercase">
                Pipeline Simulation
              </span>
            </div>
            <span className="text-[10px] sm:text-[11px] font-mono text-slate-400">Click steps to test</span>
          </div>

          {/* Steps selector buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 my-2.5 sm:my-3">
            {steps.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveStep(s.id)}
                className={`px-2 py-1.5 rounded-lg text-left font-mono text-[11px] transition-all border ${
                  activeStep === s.id
                    ? 'bg-gold-500/20 border-gold-500 text-gold-300 font-bold shadow-sm'
                    : 'bg-slate-800/60 dark:bg-navy-900/60 border-slate-700 dark:border-navy-800 text-slate-400 hover:border-slate-600'
                }`}
              >
                Step 0{s.id}
              </button>
            ))}
          </div>

          {/* Active Step Display */}
          <div className="p-3.5 sm:p-4 rounded-xl bg-slate-800/90 dark:bg-navy-900/90 border border-slate-700/80 dark:border-navy-700/80 my-auto">
            <div className="flex flex-wrap items-center justify-between gap-1 mb-1.5 sm:mb-2">
              <h5 className="font-bold text-xs sm:text-sm text-white flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-gold-400 shrink-0" />
                {steps[activeStep - 1].title}
              </h5>
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono border ${steps[activeStep - 1].tagColor}`}>
                {steps[activeStep - 1].tag}
              </span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400 mb-2.5">{steps[activeStep - 1].subtitle}</p>
            <div className="p-2.5 sm:p-3 rounded-lg bg-slate-950 dark:bg-navy-950 border border-slate-800 dark:border-navy-800 font-mono text-[11px] sm:text-xs text-gold-300 select-all overflow-x-auto">
              {steps[activeStep - 1].snippet}
            </div>
          </div>

          {/* Bottom Latency Guarantee */}
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] sm:text-xs font-mono pt-2.5 sm:pt-3 border-t border-slate-800 dark:border-navy-800 text-slate-400">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Vault Latency: <strong className="text-white">&lt; 1.2ms</strong></span>
            </span>
            <span className="text-gold-400 font-bold">Zero Raw Data Retention</span>
          </div>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
